import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle,
  DrawerDescription, DrawerFooter, DrawerClose
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  GraduationCap, X, Send, Lightbulb, Brain,
  FileQuestion, AlertCircle, Loader2, Sparkles, Zap,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface AICoachDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mcqId: string;
  topic: string;
  stem: string;
  selectedOption: string;
  correctOption: string;
  explanation: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  cached?: boolean;
}

type DepthLevel = 'eli5' | 'basic' | 'intermediate' | 'advanced';

const DEPTH_OPTIONS: { value: DepthLevel; label: string; description: string }[] = [
  { value: 'eli5', label: 'ELI5', description: 'Simple analogy for med students new to topic' },
  { value: 'basic', label: 'Basic', description: 'Junior med students (Y1–3)' },
  { value: 'intermediate', label: 'Intermediate', description: 'Senior students / interns' },
  { value: 'advanced', label: 'Advanced', description: 'Residents / board prep' },
];

export const AICoachDrawer = ({
  open, onOpenChange, mcqId, topic, stem,
  selectedOption, correctOption, explanation
}: AICoachDrawerProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [depth, setDepth] = useState<DepthLevel>('intermediate');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasAutoSent = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset on close so reopening starts fresh
  useEffect(() => {
    if (!open) {
      hasAutoSent.current = false;
      setMessages([]);
      setConversationId(null);
      setError(null);
      setInputValue('');
    }
  }, [open]);

  // Auto-send seed message on first open
  useEffect(() => {
    if (open && !hasAutoSent.current) {
      hasAutoSent.current = true;
      sendCoachMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sendCoachMessage = async (quickChip?: string, customMessage?: string, depthOverride?: DepthLevel) => {
    setIsLoading(true);
    setError(null);

    const messageContent = customMessage || inputValue;
    if (!messageContent && !quickChip && conversationId) {
      setIsLoading(false);
      return;
    }

    if (messageContent && conversationId) {
      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: messageContent,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMsg]);
      setInputValue('');
    }

    try {
      const session = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-mcq-coach`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mcqId, topic, stem, selectedOption, correctOption, explanation,
            message: messageContent, conversationId, quickChip,
            depthLevel: depthOverride || depth,
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) throw new Error('Rate limit exceeded. Please wait a moment and try again.');
        if (response.status === 402) throw new Error('AI credits exhausted. Please add more credits.');
        throw new Error('Failed to get coaching response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      const assistantId = crypto.randomUUID();
      let assistantContent = '';
      let wasCached = false;

      setMessages(prev => [...prev, {
        id: assistantId, role: 'assistant', content: '', timestamp: new Date(),
      }]);

      if (reader) {
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                assistantContent += parsed.content;
                if (parsed.cached) wasCached = true;
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, content: assistantContent, cached: wasCached } : m
                ));
              }
              if (parsed.conversationId && !conversationId) {
                setConversationId(parsed.conversationId);
              }
            } catch (_e) { /* skip */ }
          }
        }
      }
    } catch (err: any) {
      console.error('Coach error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickChip = (chip: string) => sendCoachMessage(chip);

  const handleDepthChange = (newDepth: DepthLevel) => {
    if (newDepth === depth || isLoading) return;
    setDepth(newDepth);
    // Regenerate seed at new depth
    if (messages.length > 0) {
      setMessages([]);
      setConversationId(null);
      hasAutoSent.current = true;
      sendCoachMessage(undefined, undefined, newDepth);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendCoachMessage();
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="fixed right-0 top-0 bottom-0 w-full max-w-[600px] rounded-l-[10px] flex flex-col">
        {/* Header */}
        <DrawerHeader className="border-b bg-gradient-to-br from-primary/5 to-transparent">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center ring-1 ring-primary/20">
                <GraduationCap className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DrawerTitle className="text-xl flex items-center gap-2">
                  AI Coach
                  <Sparkles className="h-4 w-4 text-primary" />
                </DrawerTitle>
                <DrawerDescription className="text-sm line-clamp-1">{topic}</DrawerDescription>
              </div>
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>

          {/* Depth selector */}
          <div className="mt-3">
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Depth level</p>
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              {DEPTH_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleDepthChange(opt.value)}
                  disabled={isLoading}
                  className={cn(
                    "flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    depth === opt.value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  title={opt.description}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 p-2.5 border border-amber-200 dark:border-amber-900/40">
            <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Educational use only. Not clinical advice. Verify with current guidelines.
            </p>
          </div>
        </DrawerHeader>

        {/* Chat Area */}
        <ScrollArea className="flex-1 px-4 py-3">
          {messages.length === 0 && !isLoading ? (
            <div className="text-center py-12 text-muted-foreground">
              <GraduationCap className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-sm">Preparing your tutorial...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "flex",
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[90%] rounded-2xl px-4 py-3",
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground border border-border'
                    )}
                  >
                    {message.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none
                        prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1.5 prose-headings:first:mt-0
                        prose-h2:text-sm prose-h2:text-primary prose-h2:uppercase prose-h2:tracking-wide
                        prose-h3:text-sm
                        prose-p:my-1.5 prose-p:leading-relaxed
                        prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
                        prose-strong:text-foreground prose-strong:font-semibold
                        prose-code:bg-background prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none">
                        <ReactMarkdown>{message.content || '...'}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-[10px] opacity-60">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {message.cached && (
                        <Badge variant="secondary" className="h-4 px-1.5 text-[9px] gap-1">
                          <Zap className="h-2.5 w-2.5" />
                          Cached
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl px-4 py-3 border border-border">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Quick Action Chips */}
        {conversationId && (
          <div className="px-4 pb-2 pt-1 border-t bg-muted/30">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5 mt-2">
              Quick actions
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Button
                variant="outline" size="sm"
                onClick={() => handleQuickChip('why_others_wrong')}
                disabled={isLoading}
                className="h-7 text-xs"
              >
                <Lightbulb className="h-3 w-3 mr-1" />
                Why others are wrong
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => handleQuickChip('mnemonic')}
                disabled={isLoading}
                className="h-7 text-xs"
              >
                <Brain className="h-3 w-3 mr-1" />
                Mnemonic
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => handleQuickChip('mini_mcqs')}
                disabled={isLoading}
                className="h-7 text-xs"
              >
                <FileQuestion className="h-3 w-3 mr-1" />
                3 mini-MCQs
              </Button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <DrawerFooter className="border-t pt-3">
          {error && (
            <div className="mb-2 p-2 rounded bg-destructive/10 text-destructive text-sm flex items-center justify-between">
              <span className="text-xs">{error}</span>
              <Button variant="ghost" size="sm" onClick={() => sendCoachMessage()} className="text-xs h-7">
                Retry
              </Button>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              placeholder={conversationId ? "Ask a follow-up question..." : "Loading tutorial..."}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || !conversationId}
              className="flex-1"
            />
            <Button
              onClick={() => sendCoachMessage()}
              disabled={isLoading || !inputValue.trim()}
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
